import { SURVEY_WORK_SERVICE_TYPE } from './surveyWorkServices'

/**
 * Stored values for `app/v1/surveyWorks` document field `status` (Firestore).
 * Several values represent an internal pipeline; the UI shows them as one "in progress" state.
 */
export const SURVEY_WORK_STATUS = {
  NEW: 'new',
  IN_PROGRESS: 'inProgress',
  PENDING_REVISION: 'pendingRevision',
  REVIEWING: 'reviewing',
  COMPLETED: 'completed'
}

/**
 * Firestore field on `app/v1/surveyWorks` documents.
 * Set to `true` from the field-survey setup view when a work is created or edited;
 * cloud functions notify the assigned technician and reset it to `false`.
 */
export const SURVEY_WORK_SHOULD_NOTIFY = 'shouldNotify'

/** Firestore values that share the same chip label and style as {@link SURVEY_WORK_STATUS.IN_PROGRESS}. */
const UI_IN_PROGRESS_GROUP = new Set([
  SURVEY_WORK_STATUS.IN_PROGRESS,
  SURVEY_WORK_STATUS.PENDING_REVISION,
  SURVEY_WORK_STATUS.REVIEWING
])

/** Legacy Firestore values grouped with in-progress in the UI. */
const LEGACY_IN_PROGRESS_ALIASES = new Set(['assigned', 'inReview'])

/** UI-only status when field survey is done but the Keystone audit report is missing or unpublished. */
export const SURVEY_WORK_UI_STATUS = {
  NEW: SURVEY_WORK_STATUS.NEW,
  IN_PROGRESS: SURVEY_WORK_STATUS.IN_PROGRESS,
  COMPLETED: SURVEY_WORK_STATUS.COMPLETED,
  REPORT_PENDING: 'reportPending'
}

/**
 * @typedef {'new'|'inProgress'|'completed'|'reportPending'} SurveyWorkUiStatus
 */

/**
 * True when a survey work can be deleted by unchecking its service
 * (empty/missing status is treated as new).
 *
 * @param {string|undefined|null} raw
 * @returns {boolean}
 */
export function isSurveyWorkNewStatus (raw) {
  if (raw == null) return true
  const s = String(raw).trim()
  return s === '' || s === SURVEY_WORK_STATUS.NEW
}

/**
 * Maps raw Firestore status to a UI bucket (i18n key + CSS class suffix).
 *
 * @param {string|undefined|null} raw
 * @returns {SurveyWorkUiStatus}
 */
export function surveyWorkStatusToUi (raw) {
  const s = raw != null && String(raw).trim() !== '' ? String(raw) : SURVEY_WORK_STATUS.NEW
  if (UI_IN_PROGRESS_GROUP.has(s) || LEGACY_IN_PROGRESS_ALIASES.has(s)) {
    return SURVEY_WORK_UI_STATUS.IN_PROGRESS
  }
  if (s === SURVEY_WORK_STATUS.COMPLETED) {
    return SURVEY_WORK_UI_STATUS.COMPLETED
  }
  if (s === SURVEY_WORK_STATUS.NEW) {
    return SURVEY_WORK_UI_STATUS.NEW
  }
  return SURVEY_WORK_UI_STATUS.NEW
}

/**
 * Keystone entity id stored on `surveyWorks.auditId` / `surveyWorks.uniformityId`.
 * @param {unknown} raw
 * @returns {string}
 */
export function normalizeSurveyWorkReportId (raw) {
  if (raw === undefined || raw === null) return ''
  return String(raw).trim()
}

/**
 * @param {{ isPublished?: boolean }|null|undefined} linkedReport
 * @returns {boolean}
 */
export function isSurveyWorkReportPublished (linkedReport) {
  return linkedReport != null && linkedReport.isPublished === true
}

/**
 * Keystone report linked to a surveyWork: Audit.id for fieldSurvey, Uniformity.id for uniformity.
 *
 * @param {{ serviceType?: string|null, auditId?: string|null, uniformityId?: string|null }} work
 * @param {{ audits?: Array<{ id?: string|null }>, uniformities?: Array<{ id?: string|null }> }} reports
 * @returns {{ isPublished?: boolean }|null}
 */
export function linkedReportForSurveyWork (work, reports) {
  if (!work) return null
  const audits = (reports && reports.audits) || []
  const uniformities = (reports && reports.uniformities) || []
  if (work.serviceType === SURVEY_WORK_SERVICE_TYPE.uniformity) {
    const workId = normalizeSurveyWorkReportId(work.uniformityId)
    if (!workId) return null
    return uniformities.find((item) => normalizeSurveyWorkReportId(item.id) === workId) || null
  }
  const workId = normalizeSurveyWorkReportId(work.auditId)
  if (!workId) return null
  return audits.find((item) => normalizeSurveyWorkReportId(item.id) === workId) || null
}

/**
 * Resolves the chip status shown on {@link FieldSurveyStatusCard}.
 * When the work is completed, shows {@link SURVEY_WORK_UI_STATUS.REPORT_PENDING}
 * until the linked Keystone report exists and is published.
 *
 * @param {{ status?: string|null }} work
 * @param {{ isPublished?: boolean }|null|undefined} linkedReport
 * @returns {SurveyWorkUiStatus}
 */
export function resolveSurveyWorkUiStatus (work, linkedReport) {
  const baseStatus = surveyWorkStatusToUi(work && work.status)
  if (baseStatus !== SURVEY_WORK_UI_STATUS.COMPLETED) {
    return baseStatus
  }
  if (!isSurveyWorkReportPublished(linkedReport)) {
    return SURVEY_WORK_UI_STATUS.REPORT_PENDING
  }
  return SURVEY_WORK_UI_STATUS.COMPLETED
}



// WEBPACK FOOTER //
// ./src/lib/surveyWorkStatus.js